import React from 'react';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { PRICING_PLANS, PRICING_FAQ } from '../data/platform';
import { PRICING_DEMO_URL, GET_STARTED_URL } from '../data/dgpt';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { PageHero } from '../components/platform/PageHero';
import { CtaButton } from '../components/ui/CtaButton';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { FaqList } from '../components/ui/FaqList';
import { ProcessCta } from '../components/platform/ProcessCta';
import { cn } from '../src/lib/utils';
export function Pricing() {
  usePageMeta({
    title: 'Pricing | DiligenceGPT™',
    description: 'DiligenceGPT™ pricing: Free, Plus ($199/mo), Pro ($499/mo) and Elite ($999/mo) plans with unlimited deals, plus bespoke Enterprise deployments. Start free or book a demo.',
    path: '/pricing',
    jsonLd: [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Pricing',
        item: canonicalUrl('/pricing')
      }]
    }]
  });
  return <main id="main" data-id="element-1967" data-mp="14pliowbliuzm">
      <PageHero headingId="pricing-heading" breadcrumb="Pricing" eyebrow="Pricing" title="Start With One Process. Grow From There." description="Every plan includes unlimited deals. Start free, upgrade as your team grows, or book a demo for a bespoke Enterprise deployment." data-id="element-1968" data-mp="14q526hgw9rfr" />

      <section aria-label="Plans" className="border-b border-hairline-soft bg-surface" data-id="element-1969" data-mp="1nn68q6q3jzlt">
        <div className="mx-auto w-full max-w-[1320px] px-4 py-16 sm:px-6 lg:px-8 lg:py-20" data-id="element-1970" data-mp="1slg4ojjnb48h">
          <ul className="grid gap-4 pt-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" data-id="element-1971" data-mp="2gbahx27vmut9">
            {PRICING_PLANS.map((plan, index) => <Reveal key={plan.name} as="li" delay={index * 0.05} className={cn('relative flex flex-col rounded-2xl border bg-panel p-6', plan.highlighted ? 'border-lime shadow-[0_30px_80px_-50px_rgba(32,22,95,0.5)]' : 'border-hairline')} data-id="element-1972" data-mp="2l326563k8og8" data-mpi={index}>
                {plan.highlighted ? <span className="absolute -top-3 left-6 rounded-full bg-lime px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-white" data-id="element-1973" data-mp="2gxpxtg328112">Most popular</span> : null}
                <h2 className="text-[13px] font-semibold uppercase tracking-[0.12em] text-text-tertiary" data-id="element-1974" data-mp="276vdih9djbi9">{plan.name}</h2>
                <p className="sf-display mt-3 text-[34px] leading-none tracking-[-0.02em] text-text-primary" data-id="element-1975" data-mp="22790a7tjpv9g">
                  {plan.price ?? 'Custom'}
                </p>
                <p className="mt-2 text-sm text-text-tertiary" data-id="element-1976" data-mp="1t7c83w7y6e9n">{plan.billing}</p>
                <ul className="mt-6 flex flex-col gap-2.5 border-t border-hairline-soft pt-6" data-id="element-1977" data-mp="1oalq33cl18ll">
                  {plan.features.map(feature => <li key={feature} className="flex items-start gap-2.5 text-sm text-text-primary" data-id="element-1978" data-mp="3tw1h0dej1hr">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-lime" aria-hidden="true" data-id="element-1979" data-mp="8sfqj9naktec" />
                      {feature}
                    </li>)}
                </ul>
                <div className="mt-auto pt-8" data-id="element-1980" data-mp="hoyhwhfvk98t">
                  <CtaButton href={plan.cta.kind === 'demo' ? PRICING_DEMO_URL : GET_STARTED_URL} variant={plan.highlighted ? 'primary' : 'outline'} size="lg" event={plan.cta.kind === 'demo' ? 'enterprise_demo_click' : 'primary_cta_click'} eventParams={{
                placement: `pricing_${plan.name.toLowerCase()}`,
                cta: plan.cta.kind
              }} className="w-full" data-id="element-1981" data-mp="3h7haobg7c342">
                    {plan.cta.label}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1982" data-mp="32fyykoqgqspz" />
                  </CtaButton>
                </div>
              </Reveal>)}
          </ul>
        </div>
      </section>

      <section aria-labelledby="pricing-faq-heading" className="border-b border-hairline-soft bg-ink" data-id="element-1983" data-mp="2efn6u9h050sa">
        <div className="mx-auto grid w-full max-w-[1320px] gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:px-8 lg:py-24" data-id="element-1984" data-mp="2t419okef4p8u">
          <SectionHeading eyebrow="FAQ" headingId="pricing-faq-heading" title="Pricing questions." data-id="element-1985" data-mp="322lk6y9trslz" />
          <Reveal data-id="element-1986" data-mp="2x538j04fazaa">
            <FaqList items={PRICING_FAQ} data-id="element-1987" data-mp="2x3iercpxnkdh" />
          </Reveal>
        </div>
      </section>

      <ProcessCta placement="pricing" data-id="element-1988" data-mp="3c0j81knrvne2" />
    </main>;
}